import { useState } from 'react';
import PropTypes from 'prop-types';
import toast from 'react-hot-toast';
import { Pencil } from 'lucide-react';
import { formatPct } from './mediaPlanningView';
import { useSetPlanMutation } from '../../queries/mediaPlanning';

/**
 * Inline editor for a brand's target marketing % for the shown month. Stored
 * per (brand, month); the "Aplicar al resto del año" checkbox fills this month
 * forward to December (never past months).
 */
export default function MarketingPctEditor({ brandId, month, marketingPct }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [restOfYear, setRestOfYear] = useState(false);
  const setPlan = useSetPlanMutation(brandId, month);

  const start = () => {
    setDraft(marketingPct == null ? '' : String(+(marketingPct * 100).toFixed(2)));
    setRestOfYear(false);
    setOpen(true);
  };

  const save = async () => {
    const pct = parseFloat(draft);
    if (Number.isNaN(pct) || pct <= 0) {
      toast.error('Ingresá un % mayor a 0');
      return;
    }
    try {
      const res = await setPlan.mutateAsync({ month, marketing_pct: pct / 100, rest_of_year: restOfYear });
      const n = res?.months?.length ?? 1;
      toast.success(n > 1 ? `% marketing guardado en ${n} meses` : '% marketing guardado');
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.error || 'No se pudo guardar el % marketing');
    }
  };

  if (!open) {
    return (
      <span className="text-sm text-gray-500">
        % marketing objetivo{' '}
        <strong className="font-medium text-gray-700">{formatPct(marketingPct)}</strong>
        <button
          type="button"
          onClick={start}
          className="ml-1 align-middle text-gray-400 hover:text-primary"
          aria-label="Editar % marketing objetivo"
          title="Editar % marketing objetivo"
        >
          <Pencil size={13} />
        </button>
      </span>
    );
  }

  return (
    <span className="inline-flex flex-wrap items-center gap-2 rounded-lg bg-gray-50 px-2.5 py-1.5 text-sm">
      <span className="text-gray-500">% marketing objetivo</span>
      <span className="inline-flex items-center">
        <input
          type="number"
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') save();
            if (e.key === 'Escape') setOpen(false);
          }}
          className="w-16 rounded border border-primary px-1.5 py-0.5 text-right"
          step="0.5"
          min="0"
          aria-label="% marketing objetivo"
        />
        <span className="ml-0.5 text-gray-500">%</span>
      </span>
      <label className="inline-flex items-center gap-1 text-xs text-gray-600">
        <input type="checkbox" checked={restOfYear} onChange={(e) => setRestOfYear(e.target.checked)} />
        Aplicar al resto del año
      </label>
      <button
        type="button"
        onClick={save}
        disabled={setPlan.isPending}
        className="rounded-md bg-primary px-2 py-0.5 text-xs font-medium text-white hover:bg-primary/90 disabled:opacity-50"
      >
        {setPlan.isPending ? 'Guardando…' : 'Guardar'}
      </button>
      <button type="button" onClick={() => setOpen(false)} className="text-xs text-gray-500 hover:text-gray-700">
        Cancelar
      </button>
    </span>
  );
}

MarketingPctEditor.propTypes = {
  brandId: PropTypes.number.isRequired,
  month: PropTypes.string.isRequired,
  marketingPct: PropTypes.number,
};
